import { useState, useRef, useEffect, useMemo } from 'react'
import { Columns3, Lock, GripVertical, Search, RotateCcw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import type { Modifier } from '@dnd-kit/core'
import type { DataTableColumn } from './data-table'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

// Restrict drag to the vertical axis within the parent container
const restrictToVerticalAxis: Modifier = ({ transform }) => ({
  ...transform,
  x: 0,
})

interface ColumnToggleProps<T> {
  columns: DataTableColumn<T>[]
  visibility: Record<string, boolean>
  onVisibilityChange: (visibility: Record<string, boolean>) => void
  /** Column IDs that are always shown (can't be hidden, but CAN be reordered) */
  alwaysShownColumns?: string[]
  /** Ordered column IDs - determines display order in the table */
  columnOrder?: string[]
  /** Called when user reorders columns via drag-and-drop */
  onColumnOrderChange?: (order: string[]) => void
}

function SortableColumnRow<T>({
  col,
  isVisible,
  isLocked,
  toggle,
  dragDisabled = false,
}: {
  col: DataTableColumn<T>
  isVisible: boolean
  isLocked: boolean
  toggle: (id: string) => void
  dragDisabled?: boolean
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: col.id,
    disabled: dragDisabled,
  })

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
  }

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className="flex items-center gap-1 px-1.5 py-1.5 transition-all duration-100 hover:bg-[var(--bg-subtle)]"
      data-dragging={isDragging || undefined}
    >
      <button
        type="button"
        disabled={dragDisabled}
        className={cn(
          'flex-shrink-0 rounded p-0.5 text-[var(--ink-3)] touch-none',
          dragDisabled
            ? 'cursor-default opacity-30'
            : 'cursor-grab active:cursor-grabbing',
        )}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="h-2.5 w-2.5" />
      </button>
      <div className="flex items-center justify-between flex-1 min-w-0">
        <span
          className={cn(
            "cv-data-table-body truncate",
            isVisible ? 'text-[var(--ink)]' : 'text-[var(--ink-3)]',
          )}
        >
          {col.header}
        </span>
        {isLocked ? (
          <Lock className="h-3 w-3 shrink-0 text-[var(--ink-3)]" />
        ) : (
          <div
            data-testid={`column-toggle-${col.id}`}
            className={cn(
              'flex h-[14px] w-[14px] shrink-0 cursor-pointer items-center justify-center rounded-[2px] border-[0.5px] transition-all duration-100',
              isVisible
                ? 'border-transparent bg-[var(--green)]'
                : 'border-[var(--rule-heavy)] bg-transparent',
            )}
            onClick={() => toggle(col.id)}
          >
            {isVisible && (
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none" stroke="white" strokeWidth="1.5" strokeLinecap="round">
                <polyline points="1,4 3,6.5 7,1.5" />
              </svg>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function ColumnToggle<T>({
  columns,
  // Default to an empty map so a caller that omits (or misnames) the prop
  // degrades to "nothing customised yet" instead of throwing
  // `'<col>' in undefined` from the `col.id in visibility` checks below,
  // which white-screens the whole page.
  visibility = {},
  onVisibilityChange,
  alwaysShownColumns,
  columnOrder,
  onColumnOrderChange,
}: ColumnToggleProps<T>) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const searchRef = useRef<HTMLInputElement>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // Auto-focus the search input each time the panel opens so the user can
  // type immediately. The filter reset happens in the open-toggle handler
  // (a user gesture), not here, to avoid setState-in-effect cascades.
  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => searchRef.current?.focus())
    }
  }, [open])

  function handleToggleOpen() {
    if (!open) setSearch('')
    setOpen((prev) => !prev)
  }

  function toggle(colId: string) {
    const col = columns.find((c) => c.id === colId)
    const current = colId in visibility
      ? visibility[colId] !== false
      : col?.defaultVisible !== false
    onVisibilityChange({ ...visibility, [colId]: !current })
  }

  // Reset visibility and order back to each column's schema-defined defaults
  // (empty maps let the render fall back to `defaultVisible` / natural order).
  function handleReset() {
    onVisibilityChange({})
    onColumnOrderChange?.([])
  }

  const lockedSet = useMemo(
    () => new Set(alwaysShownColumns ?? []),
    [alwaysShownColumns],
  )

  // All columns in display order (locked + optional, sorted by columnOrder)
  const orderedColumns = columnOrder
    ? [...columns].sort((a, b) => {
        const ai = columnOrder.indexOf(a.id)
        const bi = columnOrder.indexOf(b.id)
        if (ai === -1 && bi === -1) return 0
        if (ai === -1) return 1
        if (bi === -1) return -1
        return ai - bi
      })
    : columns

  // Whether the current visibility/order differs from the schema defaults.
  // Drives the "Reset to default" affordance, which only appears once the
  // user has actually changed something.
  const isModified = useMemo(() => {
    const visibilityModified = columns.some((col) => {
      if (lockedSet.has(col.id)) return false
      const isVisible = col.id in visibility
        ? visibility[col.id] !== false
        : col.defaultVisible !== false
      return isVisible !== (col.defaultVisible !== false)
    })
    if (visibilityModified) return true
    const naturalOrder = columns.map((c) => c.id).join(' ')
    const currentOrder = orderedColumns.map((c) => c.id).join(' ')
    return currentOrder !== naturalOrder
  }, [columns, visibility, orderedColumns, lockedSet])

  // Quick-search narrows the visible rows by header text or column id, so a
  // table with dozens of attribute columns stays browseable. Drag reorder is
  // suppressed while the filter is active because reordering a filtered
  // subset would silently move unrelated columns past each other.
  const trimmedSearch = search.trim().toLowerCase()
  const visibleColumns = useMemo(() => {
    if (!trimmedSearch) return orderedColumns
    return orderedColumns.filter((col) => {
      const header = typeof col.header === 'string' ? col.header : ''
      return (
        header.toLowerCase().includes(trimmedSearch)
        || col.id.toLowerCase().includes(trimmedSearch)
      )
    })
  }, [orderedColumns, trimmedSearch])
  const isFiltering = trimmedSearch.length > 0

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id || !onColumnOrderChange) return

    const ids = orderedColumns.map((c) => c.id)
    const oldIndex = ids.indexOf(active.id as string)
    const newIndex = ids.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return

    const newIds = [...ids]
    newIds.splice(oldIndex, 1)
    newIds.splice(newIndex, 0, active.id as string)

    onColumnOrderChange(newIds)
  }

  return (
    <div className="cv-data-table relative" ref={ref}>
      <button
        onClick={handleToggleOpen}
        className="cv-data-table-compact flex cursor-pointer items-center gap-1.5 rounded-[4px] border-[0.5px] border-solid border-[var(--rule)] px-2.5 py-1 font-normal uppercase tracking-[0.1em] text-[var(--ink-3)] transition-all duration-150 hover:bg-[var(--bg-subtle)]"
      >
        <Columns3 className="h-3 w-3" />
        {t('common.columns', { defaultValue: 'Columns' })}
      </button>
      {open && (
        <div
          className="animate-[cvPanelEnter_0.18s_cubic-bezier(0.16,1,0.3,1)] absolute right-0 top-full z-50 mt-1 flex max-h-[min(24rem,calc(100vh-12rem))] w-[220px] flex-col rounded-[8px] border-[0.5px] border-[var(--rule-strong)] bg-[var(--bg)] py-2 shadow-[var(--shadow-float)]"
        >
          <div className="cv-data-table-compact px-3 pb-2 uppercase tracking-[0.12em] text-[var(--ink-3)]">
            Visible columns
          </div>
          <div className="px-2 pb-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-[var(--ink-3)]" />
              <Input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('common.search', { defaultValue: 'Search' })}
                aria-label={t('common.search', { defaultValue: 'Search' })}
                className="cv-data-table-body h-7 pl-7 pr-2"
              />
            </div>
          </div>

          <div className="flex-1 overflow-x-hidden overflow-y-auto pr-2 [scrollbar-gutter:stable]">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis]}
            >
              <SortableContext
                items={visibleColumns.map((c) => c.id)}
                strategy={verticalListSortingStrategy}
                disabled={isFiltering}
              >
                {visibleColumns.length === 0 ? (
                  <div className="cv-data-table-body px-3 py-3 text-center text-[var(--ink-3)]">
                    No matching columns
                  </div>
                ) : (
                  visibleColumns.map((col) => {
                    const isLocked = lockedSet.has(col.id)
                    const isVisible = isLocked
                      ? true
                      : col.id in visibility
                        ? visibility[col.id] !== false
                        : col.defaultVisible !== false
                    return (
                      <SortableColumnRow
                        key={col.id}
                        col={col}
                        isVisible={isVisible}
                        isLocked={isLocked}
                        toggle={toggle}
                        dragDisabled={isFiltering}
                      />
                    )
                  })
                )}
              </SortableContext>
            </DndContext>
          </div>

          {isModified && (
            <div className="mt-1 border-t-[0.5px] border-[var(--rule)] px-2 pt-2">
              <Button
                type="button"
                variant="ghost"
                size="xs"
                onClick={handleReset}
                className="w-full justify-center gap-1.5 text-[var(--ink-3)]"
              >
                <RotateCcw className="h-3 w-3" />
                {t('common.resetToDefault', { defaultValue: 'Reset to default' })}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
